/**
 * Criterion Form Component
 *
 * Form for creating/editing a criterion with structured definition builder.
 */

import { useState, useEffect } from 'react';
import { Sparkles, Plus, X, AlertTriangle, Eye } from 'lucide-react';
import { StructuredDefinitionBuilder } from './StructuredDefinitionBuilder';
import { SearchTermEditor } from './SearchTermEditor';
import type {
  Criterion,
  CriterionFormData,
  CriterionType,
  CriterionCategory,
  StructuredDefinition,
  CATEGORY_LABELS,
} from '../../types';

interface CriterionFormProps {
  criterion?: Criterion | null;
  type: CriterionType;
  protocolId: string;
  onSubmit: (data: CriterionFormData) => Promise<void>;
  onCancel: () => void;
  onGenerateSearchTerms: (text: string, definition: StructuredDefinition) => Promise<string[]>;
  isLoading?: boolean;
  /** Initial data to pre-fill the form (e.g., from a template) */
  initialData?: Partial<CriterionFormData> | null;
}

const CATEGORIES: Array<{ value: CriterionCategory; label: string }> = [
  { value: 'demographics', label: 'Demographics' },
  { value: 'medical_history', label: 'Medical History' },
  { value: 'medications', label: 'Medications' },
  { value: 'laboratory', label: 'Laboratory' },
  { value: 'procedures', label: 'Procedures' },
  { value: 'lifestyle', label: 'Lifestyle' },
  { value: 'reproductive', label: 'Reproductive' },
  { value: 'consent', label: 'Consent' },
  { value: 'other', label: 'Other' },
];

export function CriterionForm({
  criterion,
  type,
  protocolId,
  onSubmit,
  onCancel,
  onGenerateSearchTerms,
  isLoading = false,
  initialData = null,
}: CriterionFormProps) {
  const [formData, setFormData] = useState<CriterionFormData>({
    type,
    criterion_number: '',
    category: 'medical_history',
    criterion_text: '',
    structured_definition: {
      target_type: 'condition',
      target_value: '',
      codes: [],
      constraints: [],
      negation: false,
    },
    search_terms: [],
    is_critical: false,
    requires_manual_review: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generatingTerms, setGeneratingTerms] = useState(false);

  // Populate form when criterion prop changes or initialData is provided
  useEffect(() => {
    if (criterion) {
      setFormData({
        type: criterion.type,
        criterion_number: criterion.criterion_number,
        category: criterion.category,
        criterion_text: criterion.criterion_text,
        structured_definition: criterion.structured_definition,
        search_terms: criterion.search_terms || [],
        is_critical: criterion.is_critical,
        requires_manual_review: criterion.requires_manual_review,
      });
    } else if (initialData) {
      // Pre-fill from template or other initial data
      setFormData(prev => ({
        ...prev,
        ...initialData,
        type: initialData.type || type,
        structured_definition: initialData.structured_definition || prev.structured_definition,
        search_terms: initialData.search_terms || [],
      }));
    }
  }, [criterion, initialData, type]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type: inputType } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: inputType === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleStructuredDefinitionChange = (definition: StructuredDefinition) => {
    setFormData(prev => ({ ...prev, structured_definition: definition }));
  };

  const handleSearchTermsChange = (terms: string[]) => {
    setFormData(prev => ({ ...prev, search_terms: terms }));
  };

  const handleGenerateSearchTerms = async () => {
    if (!formData.criterion_text.trim()) {
      setErrors(prev => ({ ...prev, criterion_text: 'Enter criterion text first' }));
      return;
    }

    setGeneratingTerms(true);
    try {
      const terms = await onGenerateSearchTerms(formData.criterion_text, formData.structured_definition);
      setFormData(prev => ({ ...prev, search_terms: terms }));
    } catch (err) {
      console.error('Error generating search terms:', err);
    } finally {
      setGeneratingTerms(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.criterion_number.trim()) {
      newErrors.criterion_number = 'Criterion number is required';
    }

    if (!formData.criterion_text.trim()) {
      newErrors.criterion_text = 'Criterion text is required';
    }

    if (!formData.structured_definition.target_value.trim()) {
      newErrors.structured_definition = 'Target value is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      await onSubmit(formData);
    } catch (err: any) {
      setErrors({ submit: err.message || 'Failed to save criterion' });
    }
  };

  const isInclusion = type === 'inclusion';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.submit && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-300">
          {errors.submit}
        </div>
      )}

      {/* Type indicator */}
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
          isInclusion ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
        }`}
      >
        {isInclusion ? 'Inclusion Criterion' : 'Exclusion Criterion'}
      </div>

      {/* Basic fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="criterion_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Criterion Number <span className="text-red-500 dark:text-red-400">*</span>
          </label>
          <input
            type="text"
            id="criterion_number"
            name="criterion_number"
            value={formData.criterion_number}
            onChange={handleChange}
            placeholder={isInclusion ? 'e.g., I1, I2' : 'e.g., E1, E2'}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.criterion_number ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`}
          />
          {errors.criterion_number && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.criterion_number}</p>
          )}
        </div>

        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Category <span className="text-red-500 dark:text-red-400">*</span>
          </label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Criterion text */}
      <div>
        <label htmlFor="criterion_text" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Criterion Text <span className="text-red-500 dark:text-red-400">*</span>
        </label>
        <textarea
          id="criterion_text"
          name="criterion_text"
          value={formData.criterion_text}
          onChange={handleChange}
          rows={3}
          placeholder="Enter the exact criterion text from the protocol..."
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.criterion_text ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
          } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`}
        />
        {errors.criterion_text && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.criterion_text}</p>
        )}
      </div>

      {/* Structured definition */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Structured Definition <span className="text-red-500 dark:text-red-400">*</span>
        </label>
        <StructuredDefinitionBuilder
          value={formData.structured_definition}
          onChange={handleStructuredDefinitionChange}
          error={errors.structured_definition}
        />
      </div>

      {/* Search terms */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Search Terms</label>
          <button
            type="button"
            onClick={handleGenerateSearchTerms}
            disabled={generatingTerms || !formData.criterion_text.trim()}
            className="inline-flex items-center gap-1 px-3 py-1 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generatingTerms ? (
              <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Sparkles size={14} />
            )}
            Generate with AI
          </button>
        </div>
        <SearchTermEditor
          terms={formData.search_terms || []}
          onChange={handleSearchTermsChange}
        />
      </div>

      {/* Flags */}
      <div className="flex flex-wrap gap-4">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="is_critical"
            checked={formData.is_critical}
            onChange={handleChange}
            className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-amber-600 dark:text-amber-400 focus:ring-amber-500"
          />
          <AlertTriangle size={16} className="text-amber-500 dark:text-amber-400" />
          <span className="text-sm text-gray-700 dark:text-gray-300">Critical (immediate disqualifier)</span>
        </label>

        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="requires_manual_review"
            checked={formData.requires_manual_review}
            onChange={handleChange}
            className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 dark:text-primary-400 focus:ring-primary-500"
          />
          <Eye size={16} className="text-primary-500 dark:text-primary-400" />
          <span className="text-sm text-gray-700 dark:text-gray-300">Requires manual review</span>
        </label>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2 min-h-[44px] sm:min-h-0"
        >
          {isLoading && (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          )}
          {criterion ? 'Save Changes' : 'Add Criterion'}
        </button>
      </div>
    </form>
  );
}
